import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import styles from './styles/organizationNavigatorStyles';
import { Link, Route } from 'react-router-dom';
import AppAvatars from './AppAvatars';
import routes from '../routes';
import { getAllOrgs } from '../helpers';
import AvatarLink from './AvatarLink';
import LogoWordmark from '../../assets/wordmark-spine.svg';
import { createOrgRoute } from '../helpers/routeHelpers';
import { displayUserNameShort } from '../helpers/displayHelpers';
import Avatar from 'antd/lib/avatar/avatar';
import Icon, { TeamOutlined, SwapOutlined, HomeFilled } from '@ant-design/icons';
import { antGrey, titleStyle } from './styles/globalStyles';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';
import { setWelcomeModalOptions } from '../actions/ui';
import { selectIsStatelessServer, selectOrg, selectAppCount, selectUserIsRunwayAdmin } from '../selectors/selectors';
import { PlusOutlined } from '@ant-design/icons';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { IsMobileWrapper } from '../mediaQueriesHelpers';
import { MobileOrgOrAppPicker } from './buildDistribution/mobileComponents/MobileOrgOrAppPicker';

interface OrganizationNavigatorProps {
    onClickInviteTeam: () => void;
    onClickChangeOrg: () => void;
}

const OrganizationNavigator: React.FC<OrganizationNavigatorProps> = (props: OrganizationNavigatorProps) => {
    const dispatch = useDispatch();

    const organizations = useSelector((state: RootState) => getAllOrgs(state));
    const user = useSelector((state: RootState) => state.user);
    const isStatelessServer = useSelector((state: RootState) => selectIsStatelessServer(state));
    const appsCount = useSelector((state: RootState) => selectAppCount(state));
    const defaultOrg = useSelector((state: RootState) => selectOrg(state));
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));

    const organizationAvatars = () => {
        if (!organizations) {
            return [];
        }

        return organizations.map((org, index) => {
            const isSelected = index === 0;

            const path = createOrgRoute(org.orgId);
            const letter = org.name.charAt(0).toUpperCase();

            return (
                <li style={{ paddingTop: 16 }} key={org.id}>
                    <AvatarLink
                        key={org.id}
                        isSelected={isSelected}
                        isDisabledTreatment={false}
                        path={path}
                        letter={letter}
                        square={false}
                        imageUrl={org.orgImageUrl}
                        shouldShowBetaOverlayOnWorkflowsBetaOrgs={false}
                    />
                </li>
            );
        });
    };

    const onClickAddApps = () => {
        dispatch(setWelcomeModalOptions({ shouldShowWelcomeModal: true, shouldDirectlyShowNewAppFlow: true }));
    };

    const avatarDisplayName = displayUserNameShort(user).charAt(0).toUpperCase();

    const showChangeOrg = isStatelessServer && isRunwayAdmin;
    const showInvite = !defaultOrg.ssoSamlConfig.ssoSamlEnforced;
    const showHomeIcon = appsCount === 0;

    return (
        <IsMobileWrapper>
            {(isMobile) => (
                <div
                    style={{
                        ...styles.containerStyle,
                        ...(isMobile ?
                            {
                                width: '100%',
                                flexDirection: 'row',
                                overflow: 'initial',
                            }
                        :   {}),
                    }}
                >
                    {isMobile ?
                        <MobileOrgOrAppPicker defaultOrgId={defaultOrg.id} />
                    :   <>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                }}
                            >
                                {showHomeIcon && (
                                    <Route path={[routes.org, routes.user]} exact>
                                        <div style={{ marginTop: 24 }}>
                                            <Link to={routes.dashboard}>
                                                <HomeFilled style={{ fontSize: 18, color: antGrey.grey1 }} />
                                            </Link>
                                        </div>
                                    </Route>
                                )}
                                <AppAvatars orgId={defaultOrg.id} />
                                <div style={{ marginTop: 16 }}>
                                    <PermissionedActionButton
                                        icon={
                                            <PlusOutlined
                                                style={{ fontSize: 32, color: antGrey.grey6, lineHeight: 1.75 }}
                                            />
                                        }
                                        style={{
                                            backgroundColor: antGrey.grey8,
                                            borderStyle: 'none',
                                            borderRadius: 8,
                                            width: 48,
                                            height: 48,
                                            padding: 0,
                                        }}
                                        onClick={onClickAddApps}
                                    />
                                </div>
                                <img src={LogoWordmark} alt="Runway" style={{ marginTop: 24 }} />
                            </div>
                            <div style={styles.orgsAndAvatarContainer}>
                                <div style={styles.orgContainer}>
                                    <ul style={styles.list}>
                                        {showChangeOrg && (
                                            <NavButton
                                                displayText="Switch org"
                                                icon={SwapOutlined}
                                                onClick={props.onClickChangeOrg}
                                            />
                                        )}
                                        {showInvite && (
                                            <NavButton
                                                displayText="Invite"
                                                icon={TeamOutlined}
                                                onClick={props.onClickInviteTeam}
                                            />
                                        )}
                                        {organizationAvatars()}
                                    </ul>
                                </div>
                                <div style={{ padding: 16 }}>
                                    <Link to={routes.user}>
                                        <Avatar
                                            size={46}
                                            style={{ ...styles.userAvatar }}
                                            src={user.commsAvatars?.image48}
                                        >
                                            {avatarDisplayName}
                                        </Avatar>
                                    </Link>
                                </div>
                            </div>
                        </>
                    }
                </div>
            )}
        </IsMobileWrapper>
    );
};

interface NavButtonProps {
    displayText: string;
    icon: React.ComponentType;
    onClick: () => void;
}
const NavButton = ({ displayText, icon, onClick }: NavButtonProps) => {
    return (
        <li style={{ paddingTop: 20, paddingLeft: 16, paddingRight: 16, paddingBottom: 4 }}>
            <Button ghost style={{ width: 48, height: 48, borderRadius: 24, borderStyle: 'none' }} onClick={onClick}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Icon component={icon} style={{ fontSize: 24, color: antGrey.grey4 }} />
                    <Text
                        style={{
                            ...titleStyle,
                            color: antGrey.grey4,
                            fontSize: 9,
                        }}
                        strong
                    >
                        {displayText}
                    </Text>
                </div>
            </Button>
        </li>
    );
};

export default OrganizationNavigator;
